import { useState, useEffect, useMemo } from 'react'
import { Plus, Pencil, Loader2, CheckCircle2, AlertTriangle, Sparkles, Check } from 'lucide-react'
import * as Y from 'yjs'
import type { Idea } from '../types'
import IdeaModal from './IdeaModal'
import { categoryLabel, ideaStatusLabel } from '../lib/labels'

const CATEGORY_COLORS: Record<string, string> = {
  feature: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
  bug: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
  improvement: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  design: 'bg-pink-100 text-pink-800 dark:bg-pink-900/30 dark:text-pink-300',
  general: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300',
}

const STATUS_COLORS: Record<string, string> = {
  open: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
  ready: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  processing: 'bg-violet-100 text-violet-800 dark:bg-violet-900/30 dark:text-violet-300',
  done: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300',
  error: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
  archived: 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-500',
  suggested: 'bg-agent/15 text-agent',
}

interface IdeaPanelProps {
  ideas: Y.Array<Idea>
}

export default function IdeaPanel({ ideas }: IdeaPanelProps) {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingIdea, setEditingIdea] = useState<Idea | null>(null)
  const [ideaList, setIdeaList] = useState<Idea[]>([])

  useEffect(() => {
    const handler = () => {
      setIdeaList(ideas.toArray())
    }
    ideas.observe(handler)
    handler()
    return () => ideas.unobserve(handler)
  }, [ideas])

  const openCreate = () => {
    setEditingIdea(null)
    setIsModalOpen(true)
  }

  const openEdit = (idea: Idea) => {
    setEditingIdea(idea)
    setIsModalOpen(true)
  }

  const closeModal = () => {
    setIsModalOpen(false)
    setEditingIdea(null)
  }

  const handleSave = (idea: Idea) => {
    const arr = ideas.toArray()
    const idx = arr.findIndex((i) => i.id === idea.id)
    if (idx !== -1) {
      ideas.delete(idx, 1)
      ideas.insert(idx, [idea])
    } else {
      ideas.push([idea])
    }
  }

  const handleDelete = (id: string) => {
    const arr = ideas.toArray()
    const idx = arr.findIndex((i) => i.id === id)
    if (idx !== -1) {
      ideas.delete(idx, 1)
    }
  }

  // Aceita uma sugestão do agente: vira uma ideia normal, pronta para análise.
  const acceptSuggestion = (id: string) => {
    const arr = ideas.toArray()
    const idx = arr.findIndex((i) => i.id === id)
    if (idx !== -1) {
      ideas.delete(idx, 1)
      ideas.insert(idx, [{ ...arr[idx], status: 'ready' }])
    }
  }

  const counts = useMemo(() => {
    const c = { open: 0, ready: 0, processing: 0, done: 0, error: 0 }
    for (const i of ideaList) {
      if (i.status in c) c[i.status as keyof typeof c] += 1
    }
    return c
  }, [ideaList])

  return (
    <div className="flex flex-col h-full relative">
      <div className="px-3 py-2 border-b border-gray-200 dark:border-gray-800 flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5 min-w-0">
          {counts.processing > 0 && (
            <span className="flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-violet-100 dark:bg-violet-900/30 text-violet-700 dark:text-violet-300 font-medium">
              <Loader2 size={9} className="animate-spin" aria-hidden="true" />
              {counts.processing} em análise
            </span>
          )}
          {counts.error > 0 && (
            <span className="flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 font-medium">
              <AlertTriangle size={9} aria-hidden="true" />
              {counts.error} com erro
            </span>
          )}
        </div>
        <button
          onClick={openCreate}
          className="flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-medium text-white bg-agent hover:bg-agent/90 transition-colors focus:outline-none focus:ring-2 focus:ring-agent shrink-0"
          aria-label="Adicionar nova ideia"
        >
          <Plus size={12} aria-hidden="true" />
          Nova ideia
        </button>
      </div>

      <div className="flex-1 overflow-y-auto custom-scrollbar p-2 space-y-2">
        {ideaList.length === 0 && (
          <div className="text-center text-xs text-gray-400 dark:text-gray-600 py-4 px-2">
            Nenhuma ideia ainda. Clique em <strong>Nova ideia</strong> (ou dê duplo clique no
            canvas) e marque-a como <strong>pronta</strong> para os agentes analisarem.
          </div>
        )}
        {ideaList.map((idea) => {
          const isSuggested = idea.status === 'suggested'
          return (
          <div
            key={idea.id}
            className={`px-3 py-2 rounded-lg border bg-white dark:bg-gray-900 hover:shadow-sm transition-shadow group ${
              isSuggested
                ? 'border-dashed border-agent/50 bg-agent/5'
                : 'border-gray-200 dark:border-gray-700'
            }`}
          >
            {isSuggested && (
              <div className="flex items-center gap-1 mb-1 text-[9px] font-semibold uppercase tracking-wide text-agent">
                <Sparkles size={9} aria-hidden="true" />
                Sugerida por {idea.created_by}
              </div>
            )}
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs font-semibold text-gray-900 dark:text-white truncate">
                {idea.title}
              </span>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => openEdit(idea)}
                  className="opacity-0 group-hover:opacity-100 focus:opacity-100 p-0.5 rounded hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-400 transition-opacity"
                  title={`Editar ideia "${idea.title}"`}
                  aria-label={`Editar ideia "${idea.title}"`}
                >
                  <Pencil size={10} aria-hidden="true" />
                </button>
                <span
                  className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${
                    CATEGORY_COLORS[idea.category] || CATEGORY_COLORS.general
                  }`}
                >
                  {categoryLabel(idea.category)}
                </span>
              </div>
            </div>
            <p className="text-[11px] text-gray-600 dark:text-gray-400 line-clamp-2">
              {idea.description}
            </p>
            {isSuggested ? (
              <div className="mt-2 flex items-center gap-2">
                <button
                  onClick={() => acceptSuggestion(idea.id)}
                  className="flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-medium text-white bg-agent hover:bg-agent/90 transition-colors focus:outline-none focus:ring-2 focus:ring-agent"
                >
                  <Check size={10} aria-hidden="true" />
                  Aceitar
                </button>
                <button
                  onClick={() => handleDelete(idea.id)}
                  className="px-2 py-1 rounded-md text-[10px] font-medium text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                >
                  Descartar
                </button>
              </div>
            ) : (
              <div className="mt-1.5 flex items-center justify-between text-[10px]">
                <span className="text-gray-400 dark:text-gray-500">{idea.created_by}</span>
                <span
                  className={`flex items-center gap-1 px-1.5 py-0.5 rounded-full font-medium uppercase tracking-wide ${
                    STATUS_COLORS[idea.status] || STATUS_COLORS.open
                  }`}
                >
                  {idea.status === 'processing' && <Loader2 size={8} className="animate-spin" aria-hidden="true" />}
                  {idea.status === 'done' && <CheckCircle2 size={8} aria-hidden="true" />}
                  {idea.status === 'error' && <AlertTriangle size={8} aria-hidden="true" />}
                  {ideaStatusLabel(idea.status)}
                </span>
              </div>
            )}
          </div>
          )
        })}
      </div>

      <IdeaModal
        idea={editingIdea}
        isOpen={isModalOpen}
        onClose={closeModal}
        onSave={handleSave}
        onDelete={handleDelete}
      />
    </div>
  )
}
